import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { saveApplication } from '../api/applicant'
import { ApiError } from '../api/client'
import { useFormStore } from '../store/formStore'
import { sectionByRoute } from './sections'
import { useSession } from './session'

const SAVE_DEBOUNCE_MS = 1500

/**
 * Auto-saves the draft to the backend (spec: Verified Decision #26).
 *
 * - form_data: debounced PATCH whenever the store's formData changes, plus a
 *   keepalive flush when the tab is hidden mid-debounce.
 * - current_section: PATCHed when the route lands on a section screen, so a
 *   returning user resumes where they left off.
 *
 * Mounted by RequireAuth, so it only runs with a live session on flow screens.
 */
export function useAutoSave() {
  const formData = useFormStore((s) => s.formData)
  const autoFilledFields = useFormStore((s) => s.autoFilledFields)
  const uploadStates = useFormStore((s) => s.uploadStates)
  const submittedAt = useFormStore((s) => s.submittedAt)
  const setSaveState = useFormStore((s) => s.setSaveState)
  const { applyApplication, clearSession } = useSession()
  const location = useLocation()
  const navigate = useNavigate()

  const lastSavedRef = useRef<string | null>(null)
  const lastSectionRef = useRef<string | null>(null)
  // Keep the latest payload reachable from the visibilitychange listener.
  const payloadRef = useRef<Record<string, unknown>>({})

  const payload: Record<string, unknown> = { ...formData, autoFilledFields, uploadStates }
  const serialized = JSON.stringify(payload)
  payloadRef.current = payload

  const handleSaveError = (err: unknown) => {
    if (err instanceof ApiError && err.status === 401) {
      clearSession()
      return
    }
    if (err instanceof ApiError && err.code === 'application.already_submitted') {
      setSaveState('idle')
      navigate('/applications', { replace: true })
      return
    }
    setSaveState('error')
  }

  // Debounced form_data save.
  useEffect(() => {
    if (submittedAt) return
    // First render after mount/hydration: record the baseline, don't save.
    if (lastSavedRef.current === null) {
      lastSavedRef.current = serialized
      return
    }
    if (lastSavedRef.current === serialized) return

    const timer = setTimeout(async () => {
      setSaveState('saving')
      try {
        const { application } = await saveApplication({ form_data: payloadRef.current })
        lastSavedRef.current = serialized
        applyApplication(application)
        setSaveState('saved')
      } catch (err) {
        handleSaveError(err)
      }
    }, SAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, submittedAt])

  // Flush a pending save when the tab goes hidden (visibilitychange /
  // navigating away). keepalive lets the request outlive the page.
  useEffect(() => {
    const flush = () => {
      if (document.visibilityState !== 'hidden') return
      const current = JSON.stringify(payloadRef.current)
      if (submittedAt || lastSavedRef.current === current) return
      lastSavedRef.current = current
      void fetch(`${import.meta.env.VITE_API_URL || ''}/api/v1/applicant/application`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({ form_data: payloadRef.current }),
      })
    }
    document.addEventListener('visibilitychange', flush)
    return () => document.removeEventListener('visibilitychange', flush)
  }, [submittedAt])

  // Track the current section for resume.
  useEffect(() => {
    if (submittedAt) return
    const section = sectionByRoute(location.pathname)
    if (!section || lastSectionRef.current === section.slug) return
    lastSectionRef.current = section.slug
    saveApplication({ current_section: section.slug })
      .then(({ application }) => applyApplication(application))
      .catch(handleSaveError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, submittedAt])
}
